/* ============================================================
   screen-generate.jsx — Generate studio (login-gated)
   genLayout tweak: "classic" | "focus" | "chat"
   ============================================================ */

import api from "./api/api.js";
import { downloadGeneratedResult } from "./generate-result-actions.js";
import { normalizeImageItem } from "./api/image-view-model.js";
import CONFIG from "./config/config.js";
import { useQuery } from "@tanstack/react-query";

const GENERATE_SEED_PROMPT_KEY = "prompto.generate.seedPrompt";

function takeSeedPrompt(seedPrompt) {
  if (seedPrompt) return seedPrompt;
  if (typeof window === "undefined") return "";
  try {
    const stored = window.sessionStorage.getItem(GENERATE_SEED_PROMPT_KEY) || "";
    if (stored) window.sessionStorage.removeItem(GENERATE_SEED_PROMPT_KEY);
    return stored;
  } catch (e) {
    return "";
  }
}

function useGenEngine() {
  const [status, setStatus] = React.useState("idle"); // idle | loading | done
  const [progress, setProgress] = React.useState(0);
  const [results, setResults] = React.useState([]);
  const [history, setHistory] = React.useState([]);
  const [error, setError] = React.useState("");
  const runID = React.useRef(0);

  const run = async (cfg) => {
    const id = ++runID.current;
    setStatus("loading"); setProgress(0); setResults([]); setError("");
    try {
      const job = await api.generations.run(buildGenerationParams(cfg), {
        onProgress: (value) => {
          if (runID.current === id) setProgress(Math.max(0, Math.min(100, Number(value) || 0)));
        },
      });
      if (runID.current !== id) return;
      const source = Array.isArray(job?.results) ? job.results : (Array.isArray(job?.items) ? job.items : []);
      const batch = normalizeGenerationResults(source, cfg);
      setProgress(100);
      setResults(batch);
      setHistory((h) => [{ cfg, batch, at: Date.now() }, ...h]);
      setStatus("done");
    } catch (e) {
      if (runID.current !== id) return;
      setError(e.message || "Generation failed");
      setStatus("error");
    }
  };
  React.useEffect(() => () => { runID.current++; }, []);
  return { status, progress, results, history, error, run };
}

function normalizeGenerationResults(items, cfg) {
  const styleP = cfg.styleMeta || STYLE_PRESETS.find((s) => s.id === cfg.style) || STYLE_PRESETS[0];
  return items.map((item, index) => normalizeImageItem({
    ...item,
    id: item.id || `gen-${Date.now()}-${index}`,
    seed: item.seed || (Date.now() % 100000) + index * 131,
    palette: item.palette || styleP.palette,
    prompt: item.prompt || cfg.prompt,
    promptEn: item.promptEn || item.prompt || cfg.prompt,
    aspectRatio: item.aspectRatio || item.ar || cfg.ar,
    ar: item.ar || item.aspectRatio || cfg.ar,
    model: item.model || cfg.model,
    tags: Array.isArray(item.tags) && item.tags.length ? item.tags : [styleP.label].filter((l) => l !== "스타일 없음"),
  }, index));
}

function buildGenerationParams(cfg) {
  return {
    prompt: cfg.prompt,
    style: cfg.style,
    aspectRatio: cfg.ar,
    model: cfg.model,
    count: cfg.count,
  };
}

const RATIO_OF = (ar) => ({ "1:1": "1 / 1", "3:4": "3 / 4", "4:3": "4 / 3", "16:9": "16 / 9", "9:16": "9 / 16" }[ar] || "1 / 1");

const DEFAULT_GEN_CATALOG = {
  styles: STYLE_PRESETS.map((s) => ({ id: s.id, label: s.label, labelEn: s.labelEn, palette: s.palette })),
  aspectRatios: AR_PRESETS.map((a) => ({ id: a.id, label: a.label, ratio: a.ratio })),
  models: MODELS.map((name, index) => ({
    id: name,
    name,
    descriptionKo: MODEL_DESC.ko[index] || "",
    descriptionEn: MODEL_DESC.en[index] || MODEL_DESC.ko[index] || "",
    default: index === 0,
  })),
};
const STYLE_ORDER = new Map(STYLE_PRESETS.map((style, index) => [style.id, index]));
const ASPECT_ORDER = new Map(AR_PRESETS.map((ratio, index) => [ratio.id, index]));
const MODEL_ORDER = new Map(MODELS.map((model, index) => [model, index]));

function useGenerationCatalog() {
  const query = useQuery({
    queryKey: ["catalog", "generation"],
    queryFn: async () => normalizeGenerationCatalog(await api.catalog.all()),
    staleTime: 5 * 60_000,
  });
  return query.data || DEFAULT_GEN_CATALOG;
}

function normalizeGenerationCatalog(res) {
  const styles = Array.isArray(res?.styles) && res.styles.length
    ? res.styles.map((s) => ({
      id: s.id,
      label: s.labelKo || s.label || s.id,
      labelEn: s.labelEn || s.label || s.id,
      palette: s.palette || "candy",
    })).filter((s) => s.id).sort(byKnownOrder(STYLE_ORDER, "id"))
    : DEFAULT_GEN_CATALOG.styles;
  const aspectRatios = Array.isArray(res?.aspectRatios) && res.aspectRatios.length
    ? res.aspectRatios.map((a) => ({
      id: a.id,
      label: a.label || a.id,
      ratio: a.ratioCss || a.ratio || RATIO_OF(a.id),
    })).filter((a) => a.id).sort(byKnownOrder(ASPECT_ORDER, "id"))
    : DEFAULT_GEN_CATALOG.aspectRatios;
  const models = Array.isArray(res?.models) && res.models.length
    ? res.models.map((m, index) => ({
      id: m.id || m.name,
      name: m.name || m.id,
      descriptionKo: m.descriptionKo || (MODEL_DESC.ko[index] || ""),
      descriptionEn: m.descriptionEn || (MODEL_DESC.en[index] || m.descriptionKo || ""),
      default: Boolean(m.default),
    })).filter((m) => m.name).sort(byKnownOrder(MODEL_ORDER, "name"))
    : DEFAULT_GEN_CATALOG.models;
  return { styles, aspectRatios, models };
}

function byKnownOrder(order, key) {
  const fallback = order.size + 1000;
  return (a, b) => (order.get(a[key]) ?? fallback) - (order.get(b[key]) ?? fallback);
}

function defaultModel(models) {
  return (models.find((m) => m.default) || models[0] || DEFAULT_GEN_CATALOG.models[0]).name;
}

function styleLabel(styles, styleID, lang) {
  const style = styles.find((s) => s.id === styleID) || styles[0];
  return style ? (lang === "en" ? style.labelEn : style.label) : "";
}

/* shared control cluster */
function GenControls({ cfg, set, catalog, compact = false }) {
  const { lang, t } = useT();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 16 : 20 }}>
      <Field label={t("f_style")}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {catalog.styles.map((s) => (
            <button key={s.id} className={"chip" + (cfg.style === s.id ? " is-active" : "")}
              onClick={() => set("style", s.id)} style={{ height: 32, fontSize: 13 }}>{lang === "en" ? s.labelEn : s.label}</button>
          ))}
        </div>
      </Field>

      <Field label={t("f_ar")}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {catalog.aspectRatios.map((a) => (
            <button key={a.id} onClick={() => set("ar", a.id)} style={{
              flex: "1 1 0", minWidth: 56, height: 56, borderRadius: "var(--r-sm)",
              border: "1px solid " + (cfg.ar === a.id ? "var(--accent)" : "var(--line-2)"),
              background: cfg.ar === a.id ? "var(--accent-soft)" : "var(--paper)",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5,
              transition: "all .15s var(--ease)", cursor: "pointer",
            }}>
              <span style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ background: cfg.ar === a.id ? "var(--accent)" : "var(--ink-4)",
                  borderRadius: 3, aspectRatio: a.ratio, width: a.id === "9:16" || a.id === "3:4" ? "auto" : 20,
                  height: a.id === "9:16" || a.id === "3:4" ? 20 : "auto", maxWidth: 20, maxHeight: 20 }} />
              </span>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: cfg.ar === a.id ? "var(--accent-press)" : "var(--ink-3)" }}>{a.label}</span>
            </button>
          ))}
        </div>
      </Field>

      <Field label={t("f_model")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {catalog.models.map((m, i) => (
            <button key={m.id || m.name} onClick={() => set("model", m.name)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: "var(--r-sm)",
              border: "1px solid " + (cfg.model === m.name ? "var(--accent)" : "var(--line-2)"),
              background: cfg.model === m.name ? "var(--accent-soft)" : "var(--paper)", textAlign: "left", cursor: "pointer",
              transition: "all .15s var(--ease)",
            }}>
              <span style={{ width: 30, height: 30, borderRadius: 9, background: i === 0 ? "var(--spark)" : "var(--surface-3)",
                display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                <Icon name={i === 0 ? "sparkles" : "image"} size={16} stroke={2} style={{ color: i === 0 ? "#000" : "var(--ink-3)" }} />
              </span>
              <span style={{ flex: 1 }}>
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 700 }}>{m.name}</span>
                <span className="muted" style={{ fontSize: 12 }}>{lang === "en" ? m.descriptionEn : m.descriptionKo}</span>
              </span>
              {cfg.model === m.name && <Icon name="check" size={18} style={{ color: "var(--accent)" }} />}
            </button>
          ))}
        </div>
      </Field>

      <Field label={t("f_count", { n: cfg.count })}>
        <div style={{ display: "flex", gap: 8 }}>
          {[1, 2, 4].map((n) => (
            <button key={n} onClick={() => set("count", n)} className={"chip" + (cfg.count === n ? " is-active" : "")}
              style={{ flex: 1, justifyContent: "center", height: 38 }}>{t("count_unit", { n })}</button>
          ))}
        </div>
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink-2)", marginBottom: 9, letterSpacing: "-0.01em" }}>{label}</div>
      {children}
    </div>
  );
}

/* result tile */
function ResultTile({ r, open, onLike, liked, toast, startPrompt, onDownload }) {
  const { lang, t } = useT();
  const [hover, setHover] = React.useState(false);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ position: "relative", borderRadius: "var(--r-md)", overflow: "hidden", boxShadow: "var(--sh-2)" }}>
      <ArtImage seed={r.seed} palette={r.palette} ratio={RATIO_OF(r.ar)} radius="0" />
      <div style={{ position: "absolute", inset: 0, opacity: hover ? 1 : 0, transition: "opacity .18s var(--ease)",
        background: "linear-gradient(0deg, rgba(10,9,15,.6), transparent 55%)", display: "flex",
        alignItems: "flex-end", justifyContent: "flex-end", padding: 12, gap: 8 }}>
        {CONFIG.FEATURES.social && (
          <button onClick={() => onLike(r.id)} style={iconBtnGlass} aria-label={t("like")}>
            {liked.has(r.id) || r.liked ? <HeartSolid size={17} style={{ color: "var(--like)" }} /> : <Icon name="heart" size={17} style={{ color: "#fff" }} />}
          </button>
        )}
        {CONFIG.FEATURES.download && (
          <button onClick={() => downloadGeneratedResult(r, onDownload, toast, t("t_downloaded"))} style={iconBtnGlass} aria-label={t("download")}>
            <Icon name="download" size={17} style={{ color: "#fff" }} />
          </button>
        )}
        <button onClick={() => open(r)} style={iconBtnGlass} aria-label={lang === "en" ? "Open" : "크게보기"}>
          <Icon name="arrowUpRight" size={17} style={{ color: "#fff" }} />
        </button>
      </div>
    </div>
  );
}

/* loading skeleton */
function GenSkeleton({ count, ar, progress }) {
  const { t } = useT();
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <span style={{ width: 22, height: 22, borderRadius: "50%", border: "2.5px solid var(--accent-soft)",
          borderTopColor: "var(--accent)", animation: "spin .7s linear infinite", display: "inline-block", flex: "none" }} />
        <span style={{ fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{t("gen_loading")}</span>
        <span className="tnum muted" style={{ marginLeft: "auto", fontSize: 14, fontWeight: 700, flex: "none" }}>{Math.round(progress)}%</span>
      </div>
      <div style={{ height: 5, background: "var(--surface-3)", borderRadius: 999, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ height: "100%", width: progress + "%", background: "var(--spark)", borderRadius: 999, transition: "width .2s var(--ease)" }} />
      </div>
      <div className="results-grid" style={{ "--cols": count === 1 ? 1 : 2 }}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="shimmer" style={{ aspectRatio: RATIO_OF(ar), borderRadius: "var(--r-md)",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="sparkles" size={30} stroke={1.6} style={{ color: "var(--ink-4)", position: "relative", zIndex: 1,
              animation: "pulse 1.6s var(--ease) infinite", animationDelay: (i * 0.18) + "s" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultsArea({ eng, cfg, open, onLike, liked, toast, startPrompt, onDownload, empty }) {
  const { t } = useT();
  if (eng.status === "loading") return <GenSkeleton count={cfg.count} ar={cfg.ar} progress={eng.progress} />;
  if (eng.status === "error") {
    return (
      <div className="card" role="alert" style={{ padding: 24, borderRadius: "var(--r-lg)", textAlign: "center" }}>
        <Icon name="x" size={26} style={{ color: "var(--danger, #d33)" }} />
        <p style={{ marginTop: 10, fontWeight: 700 }}>{eng.error}</p>
        <button className="btn btn-line" onClick={() => eng.run(cfg)} style={{ marginTop: 16 }}>{t("regenerate")}</button>
      </div>
    );
  }
  if (eng.status === "done") {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span className="badge badge-new" style={{ height: 26 }}><Icon name="check" size={14} stroke={2.4} /> {t("done_badge")}</span>
            <span style={{ fontSize: 14, color: "var(--ink-2)" }}>{t("result_meta", { n: eng.results.length, model: cfg.model })}</span>
          </div>
          <button className="btn btn-line btn-sm" onClick={() => eng.run(cfg)}>
            <Icon name="refresh" size={16} /> {t("regenerate")}
          </button>
        </div>
        <div className="results-grid" style={{ "--cols": cfg.count === 1 ? 1 : 2 }}>
          {eng.results.map((r, i) => (
            <Reveal key={r.id} delay={i * 70}>
              <ResultTile r={r} open={open} onLike={onLike} liked={liked} toast={toast} startPrompt={startPrompt} onDownload={onDownload} />
            </Reveal>
          ))}
        </div>
        <p className="muted" style={{ fontSize: 13, marginTop: 16, textAlign: "center" }}>
          {t("results_hint")}
        </p>
      </div>
    );
  }
  return empty;
}

/* ---------- main studio ---------- */
function Generate({ auth, onLogin, go, open, liked, onLike, toast, seedPrompt, genLayout, onDownload }) {
  if (!auth) return <GenGate onLogin={onLogin} go={go} />;

  return <GenerateStudio {...{ open, liked, onLike, toast, seedPrompt, genLayout, onDownload }} />;
}

function GenerateStudio({ open, liked, onLike, toast, seedPrompt, genLayout, onDownload }) {
  const eng = useGenEngine();
  const catalog = useGenerationCatalog();
  const [cfg, setCfg] = React.useState({
    prompt: takeSeedPrompt(seedPrompt), style: "cinema", ar: "1:1", model: MODELS[0], count: 4,
  });
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  React.useEffect(() => { if (seedPrompt) setCfg((c) => ({ ...c, prompt: seedPrompt })); }, [seedPrompt]);
  React.useEffect(() => {
    setCfg((c) => ({
      ...c,
      style: catalog.styles.some((s) => s.id === c.style) ? c.style : catalog.styles[0].id,
      ar: catalog.aspectRatios.some((a) => a.id === c.ar) ? c.ar : catalog.aspectRatios[0].id,
      model: catalog.models.some((m) => m.name === c.model) ? c.model : defaultModel(catalog.models),
    }));
  }, [catalog]);

  const canRun = cfg.prompt.trim().length > 0 && eng.status !== "loading";
  const doRun = () => {
    if (!canRun) return;
    const styleMeta = catalog.styles.find((s) => s.id === cfg.style);
    eng.run({ ...cfg, styleMeta });
  };

  const startPrompt = (p) => { set("prompt", p); };

  if (genLayout === "chat") return <ChatStudio {...{ cfg, set, catalog, eng, doRun, canRun, open, onLike, liked, toast, onDownload }} />;
  if (genLayout === "focus") return <FocusStudio {...{ cfg, set, catalog, eng, doRun, canRun, open, onLike, liked, toast, startPrompt, onDownload }} />;
  return <ClassicStudio {...{ cfg, set, catalog, eng, doRun, canRun, open, onLike, liked, toast, startPrompt, onDownload }} />;
}

/* === Layout A: classic (sidebar + canvas) === */
function ClassicStudio({ cfg, set, catalog, eng, doRun, canRun, open, onLike, liked, toast, startPrompt, onDownload }) {
  const { t } = useT();
  return (
    <div className="container" style={{ padding: "24px 24px 64px" }}>
      <div className="studio-grid" style={{ display: "grid", gridTemplateColumns: "352px minmax(0,1fr)", gap: 28, alignItems: "start" }}>
        {/* control panel */}
        <div className="card studio-panel" style={{ padding: 20, borderRadius: "var(--r-lg)", position: "sticky", top: 84 }}>
          <h2 style={{ fontSize: 19, display: "flex", alignItems: "center", gap: 8, marginBottom: 16, whiteSpace: "nowrap" }}>
            <Icon name="wand" size={20} stroke={2} style={{ color: "var(--accent)" }} /> {t("gen_title")}
          </h2>
          <Field label={t("prompt")}>
            <PromptBox value={cfg.prompt} onChange={(v) => set("prompt", v)} onEnter={doRun} />
          </Field>
          <div style={{ height: 18 }} />
          <GenControls cfg={cfg} set={set} catalog={catalog} compact />
          <button className="btn btn-spark btn-lg" onClick={doRun} disabled={!canRun}
            style={{ width: "100%", marginTop: 22, opacity: canRun ? 1 : 0.5, cursor: canRun ? "pointer" : "not-allowed" }}>
            <Icon name="sparkles" size={19} stroke={2} /> {t("gen_run")}
            <span style={{ marginLeft: 6, opacity: .8, fontSize: 13, fontWeight: 600 }}>{t("credits", { n: cfg.count })}</span>
          </button>
        </div>

        {/* canvas */}
        <div>
          <ResultsArea eng={eng} cfg={cfg} open={open} onLike={onLike} liked={liked} toast={toast} startPrompt={startPrompt} onDownload={onDownload}
            empty={<StudioEmpty />} />
        </div>
      </div>
    </div>
  );
}

/* === Layout B: focus (centered, prompt-first) === */
function FocusStudio({ cfg, set, catalog, eng, doRun, canRun, open, onLike, liked, toast, startPrompt, onDownload }) {
  const { lang, t } = useT();
  const [showAdv, setShowAdv] = React.useState(false);
  return (
    <div className="container generate-focus-page" style={{ padding: "32px 24px 72px", maxWidth: 980 }}>
      <div className="generate-focus-header" style={{ textAlign: "center", marginBottom: 22 }}>
        <span className="badge badge-spark" style={{ height: 28, padding: "0 12px", fontSize: 13 }}>
          <Icon name="sparkles" size={14} stroke={2} /> {t("focus_badge")}
        </span>
        <h1 style={{ fontSize: "clamp(26px,3.2vw,38px)", marginTop: 14 }}>{t("focus_title")}</h1>
      </div>

      {/* big prompt */}
      <div className="card generate-focus-card" style={{ padding: 18, borderRadius: "var(--r-xl)", boxShadow: "var(--sh-2)" }}>
        <PromptBox value={cfg.prompt} onChange={(v) => set("prompt", v)} onEnter={doRun} rows={3} big />
        <div className="generate-focus-actions" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button className="chip" onClick={() => setShowAdv((v) => !v)} style={{ height: 38 }}>
            <Icon name="sliders" size={16} /> {showAdv ? t("opt_collapse") : t("opt_expand")}
          </button>
          <span className="chip" style={{ height: 38, cursor: "default" }}>{cfg.ar}</span>
          <span className="chip" style={{ height: 38, cursor: "default" }}>{styleLabel(catalog.styles, cfg.style, lang)}</span>
          <button className="btn btn-spark btn-lg" onClick={doRun} disabled={!canRun}
            style={{ marginLeft: "auto", opacity: canRun ? 1 : .5, cursor: canRun ? "pointer" : "not-allowed" }}>
            <Icon name="sparkles" size={18} stroke={2} /> {t("gen_run")}
          </button>
        </div>
        {showAdv && (
          <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--line)" }}>
            <GenControls cfg={cfg} set={set} catalog={catalog} />
          </div>
        )}
      </div>

      {/* suggestions when idle */}
      {eng.status === "idle" && (
        <div className="generate-focus-suggestions" style={{ marginTop: 24 }}>
          <div className="muted" style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{t("try_these")}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(SUGGEST[lang] || SUGGEST.ko).map((p) => (
              <button key={p} className="chip" onClick={() => startPrompt(p)} style={{ height: 36 }}>{p}</button>
            ))}
          </div>
        </div>
      )}

      <div className="generate-focus-results" style={{ marginTop: 28 }}>
        <ResultsArea eng={eng} cfg={cfg} open={open} onLike={onLike} liked={liked} toast={toast} startPrompt={startPrompt} onDownload={onDownload} empty={null} />
      </div>
    </div>
  );
}

/* === Layout C: chat (conversational) === */
function ChatStudio({ cfg, set, catalog, eng, doRun, canRun, open, onLike, liked, toast, onDownload }) {
  const { lang, t } = useT();
  const scrollRef = React.useRef(null);
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [eng.history.length, eng.status]);

  return (
    <div className="container" style={{ padding: "0 24px", maxWidth: 920 }}>
      <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 64px)" }}>
        {/* thread */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "24px 4px 24px" }}>
          {eng.history.length === 0 && eng.status === "idle" && (
            <div style={{ textAlign: "center", padding: "48px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: "var(--spark)", margin: "0 auto 16px",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="sparkles" size={28} stroke={2} style={{ color: "#000" }} />
              </div>
              <h2 style={{ fontSize: 22 }}>{t("chat_title")}</h2>
              <p className="muted" style={{ marginTop: 8 }}>{t("chat_sub")}</p>
            </div>
          )}

          {[...eng.history].reverse().map((h, idx) => (
            <div key={idx} style={{ marginBottom: 28 }}>
              {/* user bubble */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
                <div style={{ maxWidth: "78%", background: "#000", color: "#fff", padding: "12px 16px",
                  borderRadius: "18px 18px 4px 18px", fontSize: 15 }}>
                  {h.cfg.prompt}
                  <div style={{ display: "flex", gap: 6, marginTop: 8, opacity: .75, fontSize: 12 }}>
                    <span>{h.cfg.ar}</span>·<span>{styleLabel(catalog.styles, h.cfg.style, lang)}</span>·<span>{t("count_unit", { n: h.cfg.count })}</span>
                  </div>
                </div>
              </div>
              {/* ai bubble */}
              <div style={{ display: "flex", gap: 10 }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, background: "var(--spark)", flex: "none",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="sparkles" size={17} stroke={2} style={{ color: "#000" }} />
                </span>
                <div style={{ flex: 1 }}>
                  <div className="results-grid" style={{ "--cols": h.cfg.count === 1 ? 1 : 2, maxWidth: 520 }}>
                    {h.batch.map((r) => (
                      <ResultTile key={r.id} r={r} open={open} onLike={onLike} liked={liked} toast={toast} startPrompt={() => {}} onDownload={onDownload} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {eng.status === "loading" && (
            <div style={{ display: "flex", gap: 10 }}>
              <span style={{ width: 32, height: 32, borderRadius: 9, background: "var(--spark)", flex: "none",
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="sparkles" size={17} stroke={2} style={{ color: "#000" }} />
              </span>
              <div style={{ flex: 1, maxWidth: 520 }}>
                <GenSkeleton count={cfg.count} ar={cfg.ar} progress={eng.progress} />
              </div>
            </div>
          )}
        </div>

        {/* composer */}
        <div style={{ padding: "12px 0 20px", borderTop: "1px solid var(--line)", background: "var(--canvas)" }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            {catalog.styles.slice(0, 6).map((s) => (
              <button key={s.id} className={"chip" + (cfg.style === s.id ? " is-active" : "")}
                onClick={() => set("style", s.id)} style={{ height: 30, fontSize: 12.5 }}>{lang === "en" ? s.labelEn : s.label}</button>
            ))}
            <select value={cfg.ar} onChange={(e) => set("ar", e.target.value)} className="chip"
              style={{ height: 30, fontSize: 12.5, paddingRight: 8 }}>
              {catalog.aspectRatios.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, background: "var(--paper)",
            border: "1px solid var(--line-2)", borderRadius: "var(--r-lg)", padding: 10, boxShadow: "var(--sh-1)" }}>
            <textarea value={cfg.prompt} onChange={(e) => set("prompt", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doRun(); } }}
              placeholder={t("chat_ph")} rows={1}
              style={{ flex: 1, border: "none", outline: "none", resize: "none", background: "transparent",
                fontSize: 15, fontFamily: "inherit", lineHeight: 1.5, maxHeight: 120, padding: "6px 4px" }} />
            <button className="btn btn-spark btn-icon" onClick={doRun} disabled={!canRun}
              style={{ opacity: canRun ? 1 : .5, cursor: canRun ? "pointer" : "not-allowed", flex: "none" }} aria-label={t("gen_run")}>
              <Icon name="arrowRight" size={20} stroke={2.2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PromptBox({ value, onChange, onEnter, rows = 4, big = false }) {
  const { t } = useT();
  return (
    <textarea className={big ? "generate-prompt-box generate-prompt-box--big" : "generate-prompt-box"} value={value} onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onEnter(); }}
      placeholder={t("prompt_ph")}
      rows={rows}
      style={{
        width: "100%", border: "1px solid var(--line-2)", borderRadius: "var(--r-md)",
        padding: big ? "14px 16px" : "12px 14px", fontSize: big ? 17 : 15, fontFamily: "inherit",
        lineHeight: 1.6, resize: "vertical", outline: "none", background: "var(--surface-2)", color: "var(--ink)",
        minHeight: big ? 84 : 72,
      }}
      onFocus={(e) => { e.target.style.borderColor = "var(--accent)"; e.target.style.background = "var(--paper)"; }}
      onBlur={(e) => { e.target.style.borderColor = "var(--line-2)"; e.target.style.background = "var(--surface-2)"; }}
    />
  );
}

function StudioEmpty() {
  const { t } = useT();
  return (
    <div className="card" style={{ borderRadius: "var(--r-lg)", borderStyle: "dashed", padding: "64px 24px",
      textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ width: 72, height: 72, borderRadius: 20, background: "var(--k-yellow-soft)", display: "flex",
        alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
        <Icon name="image" size={34} style={{ color: "var(--accent)" }} />
      </div>
      <h3 style={{ fontSize: 21 }}>{t("studio_empty_title")}</h3>
      <p className="muted" style={{ marginTop: 8, maxWidth: 360 }}>
        {t("studio_empty_a")}<b style={{ color: "var(--ink-2)" }}>{t("gen_run")}</b>{t("studio_empty_b")}
      </p>
    </div>
  );
}

/* ---------- login gate ---------- */
function GenGate({ onLogin, go }) {
  const { t } = useT();
  return <LoginGate onLogin={onLogin} go={go} title={t("gate_title")} desc={t("gate_desc")} primaryLabel={t("gate_btn")} secondaryLabel={t("gate_browse")} />;
}

Object.assign(window, { Generate, useGenEngine, GenControls, ResultsArea, GenGate });
